/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import IdManager from 'linced-app/components/id-manager/nz-moe';
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { debounce } from '@ember/runloop';
import { observes } from '@ember-decorators/object';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';
import config from 'linced-app/config/environment';
import ApplicationStatus from 'linced-app/enums/application-status';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import State from 'linced-app/enums/enrolment-request-state';

export default class NzMoe extends IdManager {
  @service
  toaster;

  @action
  setStartDate(value) {
    this.startDate = value;
  }

  @action
  setStep(value) {
    this.step = value;
  }

  @service
  fetch;

  @service
  session;

  @service
  router;

  @service
  schoolSchema;

  EnrolmentStatus = EnrolmentStatus;
  State = State;
  entityType = 'application';

  @alias('application')
  person;

  caregiverFields = [];
  relationshipFields = [];
  @tracked step = 0;

  // isValid should return true if we have a JurisdictionalId, as it can only be set on an Application by this process.
  // It is used by the IdManager class to determine whether to include the NSN in the request.
  @computed('application.jurisdictionalId')
  get isValid() {
    return !isEmpty(this.get('application.jurisdictionalId'));
  }

  @computed('status.status', 'identifier')
  get nsnSearching() {
    if (!isEmpty(this.identifier)) {
      // If we have an Identifier, we're no longer searching.
      return false;
    }
    let status = this.get('status.status');
    if (status === State.READY) {
      // Search is complete, but we don't yet have the Identifier record.
      return true;
    }
    // Otherwise, we're searching if we don't have a Status or it says we're currently processing.
    return isEmpty(this.status) || status === State.PROCESSING;
  }

  @tracked
  learnerSearching = false;
  @tracked
  learnerResults = [];

  get firstLearnerResult() {
    return this.learnerResults[0];
  }

  @computed('step', 'nsnSearching', 'requestValid')
  get stepIsValid() {
    let enrolResponseType = 'A';
    switch (this.step) {
      case 0:
        if (!this.requestValid) {
          // requestValid dictates whether we can do a Get/CheckNSN.  Allow skipping if not valid.
          return true;
        }
        // Not allowed to continue if we're currently running a search.
        if (this.nsnSearching) {
          return false;
        }
        if (this.get('status.status') === 3) {
          // Status 3 => failed.  For now, allow the user to proceed.
          return true;
        }
        enrolResponseType = this.get('identifier.nzMoe.type');
        // Type C (differences) with no differences implies that the Application is correct,
        // but the Learner is currently enrolled elsewhere.
        if (enrolResponseType === 'C' && isEmpty(this.getDifferences())) {
          return true;
        }
        // Type A (error), Type B (NSN is correct) and E (No Results) can continue;
        // others will auto-proceed to next step on completion.
        return ['A', 'B', 'E'].includes(enrolResponseType);
      case 1:
        // "Search Hero" step either auto-proceeds (no results) or requires a user choice; disable the "next" button.
        return false;
      case 2:
        return true;
    }
    return false;
  }

  @observes('identifier')
  checkNSNResponse() {
    // If we have an Identifier, skip ahead if applicable.
    switch (this.get('identifier.nzMoe.type')) {
      case 'B':
        // NSN is correct.  Skip to Hero learner search.
        this.incrementProperty('step');
        break;
    }
  }

  @tracked
  startDate = '';
  @tracked
  processing = false;
  @tracked
  processed = false;
  error = '';
  @tracked
  contactMappingSkipped = false;

  @tracked
  foundPerson;

  @tracked
  foundPersonStatus;

  @tracked
  learner;

  @tracked
  newPersonId;
  @tracked pollTimer;
  @tracked status;
  @tracked contacts;
  @tracked medicalContacts;

  @computed('contacts.@each.processing')
  get contactMappingInProgress() {
    if (!isArray(this.contacts)) {
      return false;
    }
    return this.contacts.some((contact) => contact.processing);
  }

  @observes('step')
  stepChanged() {
    switch (this.step) {
      case 1:
        this.triggerLearnerSearch();
        break;
      case 3:
        this.process();
        break;
    }
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    this.set('step', 0);

    // Check for existing in-flight or ready-to-process results.
    this.checkStatus().then(() => {
      // If none found, trigger a Get/Check NSN.
      if (isEmpty(this.identifier) && isEmpty(this.status)) {
        if (this.requestValid) {
          this.makeRequest().then(() => {
            if (isEmpty(this.pollTimer)) {
              this.pollTimer = debounce(this, 'updateStatus', 2000, false);
            }
          });
        }
      }
    });
  }

  onComplete() {}
  onCancel() {}

  @action
  removeNSN(restart) {
    // Revert any fields which were changed by the Get/Check NSN process.
    this.application.rollbackAttributes();

    // Ensure jurisdictionalId and NSN are empty.
    this.application.set('jurisdictionalId', '');
    this.application.set('fields.nzMOE:nsn.value', '');

    // Restart Get/Check NSN if requested
    if (restart) {
      this.archiveIdentifier().then(() => {
        this.set('identifier', null);
        this.set('step', 0);
        this.makeRequest().then(() => {
          if (isEmpty(this.pollTimer)) {
            this.pollTimer = debounce(this, 'updateStatus', 2000, false);
          }
        });
      });
    }
  }

  @action
  setPersonId(personId) {
    this.application.set('personId', personId);
    return this.application.save().then(() => {
      // After persisting the personId, move to the next step.
      this.incrementProperty('step');
    });
  }

  @action
  cancel(archive) {
    if (archive && !isEmpty(this.identifier)) {
      this.archiveIdentifier();
    }
    this.onCancel();
  }

  // decline the application and return the user to the list of applications.
  @action
  decline() {
    if (!isEmpty(this.identifier)) {
      this.archiveIdentifier();
    }
    this.application.set('status', ApplicationStatus.DECLINED);
    this.application.save().then(() => {
      this.router.transitionTo('enrolment.applications');
    });
  }

  // Overrides the id-manager's archiveIdentifier to retain the Identifier record for use in subsequent steps.
  archiveIdentifier() {
    let url = `${config.APP.apiHost}/enrolments/${config.APP.apiPath}/identifiers/${this.identifier.id}/:archive`;
    return this.fetch.post(url).then(() => {
      if (!(this.isDestroyed || this.isDestroying)) {
        this.status = null;
      }
    });
  }

  triggerLearnerSearch() {
    let nsn = this.get('application.jurisdictionalId');
    if (isEmpty(nsn)) {
      // No NSN to search for; assume it's a new Learner and continue to next step.
      this.incrementProperty('step');
    } else {
      // We have an NSN.  Trigger the Hero Learner search.
      this.learnerSearching = true;

      let jurisdiction = Jurisdiction.NZ_MOE;
      this.fetch
        .request(
          `${config.APP.apiHost}/people/v4/learners/:search?query=${nsn}&jurisdiction=${jurisdiction}`,
        )
        .then((response) => {
          if (response) {
            if (!!response.learners && response.learners.length) {
              this.learnerResults = response.learners;
              let inThisSchool = findBy(
                this.learnerResults,
                'inThisSchool',
                true,
              );
              if (inThisSchool) {
                // We found an existing learner with this NSN in the current school.
                this.store
                  .findRecord('person', inThisSchool.personId)
                  .then((person) => {
                    this.foundPerson = person;
                    let schoolId = this.session.activeSchool;
                    // Enrolment stubs are already ordered, so the first one with our school ID is the current/latest here.
                    let enrolment = findBy(
                      person.get('learner.enrolments'),
                      'schoolId',
                      schoolId,
                    );
                    this.foundPersonStatus = enrolment.status;

                    this.learnerSearching = false;
                  });
              } else {
                this.learnerSearching = false;
              }
            } else {
              this.incrementProperty('step');
              this.learnerSearching = false;
            }
          } else {
            this.learnerSearching = false;
          }
        });
    }
  }

  process() {
    this.processing = true;
    this.createEnrolment(this.application, this.startDate)
      // A failed create is discarded, so the application doesn't keep it as its
      // enrolment (and send a null id for it on its next save).
      .then((enrolment) => saveOrDiscard(enrolment))
      .then((enrolment) => {
        let application = this.application;
        let newPersonId = enrolment.get('personId');
        this.newPersonId = newPersonId;

        // Revert any fields which were changed by the Get/Check NSN process.
        // This preserves the Application to Enrol as it was originally entered.
        application.rollbackAttributes();

        application.setProperties({
          status: ApplicationStatus.ACCEPTED,
          jurisdictionalId: enrolment.jurisdictionalId,
        });
        if (!isEmpty(newPersonId)) {
          application.set('personId', newPersonId);
        }
        return application.save().then(() => {
          this.onComplete();
          return this.store.findRecord('person', enrolment.get('personId'));
        });
      })
      .then((person) => {
        let { contacts, medicalContacts } = this.processContacts(false);
        if (contacts === false && medicalContacts === false) {
          this.contactMappingSkipped = true;
        } else {
          this.contacts = contacts;
          this.medicalContacts = medicalContacts;
        }
        this.processed = true;
        this.processing = false;
        this.learner = person;
      })
      .catch(() => {
        this.processing = false;
        this.toaster.error('Error processing the application.');
      });
  }
}
